import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { apiFetch } from '../api';
import { Logo } from '../components/Logo';

const s = {
  page: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #1e3a8a 0%, #1e40af 50%, #1d4ed8 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
  },
  card: {
    background: '#fff',
    borderRadius: 16,
    padding: '40px 36px',
    width: '100%',
    maxWidth: 420,
    boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
  },
  logo: { fontSize: 28, fontWeight: 800, color: '#1e40af', marginBottom: 4, letterSpacing: '-0.5px' },
  subtitle: { fontSize: 15, color: '#64748b', marginBottom: 32 },
  label: { display: 'block', fontSize: 14, fontWeight: 500, color: '#374151', marginBottom: 6 },
  input: {
    width: '100%',
    padding: '10px 14px',
    border: '1px solid #d1d5db',
    borderRadius: 8,
    fontSize: 15,
    color: '#1e293b',
    outline: 'none',
    marginBottom: 18,
  },
  btn: {
    width: '100%',
    padding: '12px',
    background: '#1e40af',
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    fontWeight: 600,
    fontSize: 16,
    cursor: 'pointer',
    marginTop: 4,
  },
  error: { background: '#fef2f2', color: '#b91c1c', padding: '10px 14px', borderRadius: 8, fontSize: 14, marginBottom: 16 },
  gdpr: {
    background: '#f8fafc',
    border: '1px solid #e2e8f0',
    borderRadius: 8,
    padding: '12px 14px',
    fontSize: 12,
    color: '#64748b',
    lineHeight: 1.6,
    marginTop: 12,
    marginBottom: 4,
  },
  gdprFlag: { marginRight: 6 },
  footer: { marginTop: 20, textAlign: 'center', fontSize: 14, color: '#64748b' },
};

export default function Signup({ onLogin }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Optional plan from the pricing page, e.g. /signup?plan=tier1
  const plan = new URLSearchParams(window.location.search).get('plan');

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    if (password !== confirm) {
      setError('Passwords do not match');
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    setLoading(true);
    try {
      const { data, error: signUpError } = await supabase.auth.signUp({ email, password });
      if (signUpError) throw signUpError;

      // With "Confirm email" disabled, signUp returns a session immediately,
      // so we can start the trial right away. (The public.users row is created
      // by the on_auth_user_created trigger.)
      if (data.session) {
        const path = plan
          ? `/auth/init-trial?plan=${encodeURIComponent(plan)}`
          : '/auth/init-trial';
        try {
          await apiFetch(path, { method: 'POST' });
        } catch (_) {
          /* non-fatal: user can still upgrade later */
        }
        if (onLogin) onLogin();
        navigate('/onboarding');
      } else {
        // Email confirmation is enabled — ask the user to confirm first.
        setError('Check your email to confirm your account, then sign in.');
      }
    } catch (err) {
      setError(err.message || 'Could not create account');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={s.page}>
      <div style={s.card}>
        <div style={s.logo}><Logo tone="color" size={28} /></div>
        <div style={s.subtitle}>Create your account — start your 14-day free trial</div>

        {error && <div style={s.error}>{error}</div>}

        <form onSubmit={handleSubmit}>
          <label style={s.label}>Email</label>
          <input
            style={s.input}
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@company.com"
            required
            autoFocus
          />
          <label style={s.label}>Password</label>
          <input
            style={s.input}
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Min. 8 characters"
            required
          />
          <label style={s.label}>Confirm password</label>
          <input
            style={s.input}
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            placeholder="••••••••"
            required
          />
          <button style={s.btn} type="submit" disabled={loading}>
            {loading ? 'Creating account…' : 'Create account'}
          </button>
        </form>

        <div style={s.gdpr}>
          <span style={s.gdprFlag}>🇪🇺</span>
          By creating an account you agree to our{' '}
          <Link to="/terms">Terms of Service</Link> and{' '}
          <Link to="/privacy">Privacy Policy</Link>.
          Your data is stored and processed within the EU in accordance with GDPR.
          We do not sell your data to third parties.
        </div>

        <div style={s.footer}>
          Already have an account? <Link to="/login">Sign in</Link>
        </div>
      </div>
    </div>
  );
}
